import { SafeError, SafePromise, SafeType } from '@benzinga/safe-await';
import { isString } from '@benzinga/utils';
import { StockSymbol, Sound } from '@benzinga/session';
import { SymbolId, Watchlist, WatchlistId, WatchlistSymbol } from './entities';

export interface IncomingHolding {
  average_buying_price: number;
  cost_basis: number;
  created_date: string;
  holding_type: string;
  isin: string;
  last_update: string;
  price: number;
  quantity: number;
  security_type: string;
}

export interface IncomingWatchlistSymbol {
  alert_id: string;
  alert_name: StockSymbol;
  count_week: number;
  holding: IncomingHolding;
  is_bz: 1 | 0;
  is_bzpro: 1 | 0;
  is_pr: 1 | 0;
  is_sec: 1 | 0;
  portfolio_id: WatchlistId;
  send_realtime: 1 | 0;
  sound: Sound;
  status: 1 | 0;
  type: 'ticker';
}

type IncomingWatchlistSymbolObject = {
  [id in WatchlistId]: IncomingWatchlistSymbol;
};

export interface IncomingWatchlist {
  alerts: IncomingWatchlistSymbolObject;
  portfolio_id: WatchlistId;
  portfolio_name: string;
  realtime_bz: 1 | 0;
  realtime_pr: 1 | 0;
  realtime_sec: 1 | 0;
  send_summary: 1 | 0;
  sound: Sound;
  status: 1 | 0;
}

interface RestfulResult<DATA> {
  data: DATA;
  response_code: number;
}

export type IncomingWatchlistSymbolResult = RestfulResult<Record<SymbolId, IncomingWatchlistSymbol>>;
export type IncomingWatchlistSymbolsResult = RestfulResult<Record<SymbolId, IncomingWatchlistSymbol>[]>;
export type IncomingWatchlistResult = RestfulResult<Record<WatchlistId, IncomingWatchlist>>;

const normalizeWatchlistSymbol = (symbol: StockSymbol): string => {
  if (isString(symbol) && symbol.includes('$')) {
    return symbol.replace('$', '') + '/USD';
  } else {
    return symbol;
  }
};

export { normalizeWatchlistSymbol };

export const parseSymbols = (alerts: Record<SymbolId, IncomingWatchlistSymbol>): WatchlistSymbol[] => {
  return Object.values(alerts).map(alert => ({
    countWeek: alert.count_week,
    holding: alert.holding
      ? {
          averageBuyingPrice: alert.holding.average_buying_price,
          costBasis: alert.holding.cost_basis,
          createdDate: alert.holding.created_date,
          holdingType: alert.holding.holding_type,
          isIn: alert.holding.isin,
          lastUpdated: alert.holding.last_update,
          price: alert.holding.price,
          quantity: alert.holding.quantity,
          securityType: alert.holding.security_type,
        }
      : undefined,
    isBZ: alert.is_bz,
    isBZPro: alert.is_bzpro,
    isPR: alert.is_pr,
    isSEC: alert.is_sec,
    sendRealtime: alert.send_realtime,
    sound: alert.sound,
    status: alert.status,
    symbol: normalizeWatchlistSymbol(alert.alert_name),
    symbolId: alert.alert_id,
    type: alert.type,
    watchlistId: alert.portfolio_id,
  }));
};

export const ingestSymbols = async (
  watchlistData: SafePromise<IncomingWatchlistSymbolsResult>,
): SafePromise<WatchlistSymbol[]> => {
  const rawWatchlists = await watchlistData;

  if (rawWatchlists.err) {
    return rawWatchlists as SafeType<WatchlistSymbol[]>;
  } else {
    if (rawWatchlists.ok?.data) {
      return { ok: rawWatchlists.ok.data.map(item => parseSymbols(item)).flat() };
    } else {
      return { err: new SafeError('did not get valid watchlist symbols', 'invalid_watchlist') };
    }
  }
};

export const ingestSymbol = async (
  watchlistSymbolData: SafePromise<IncomingWatchlistSymbolResult>,
): SafePromise<WatchlistSymbol> => {
  const rawWatchlistSymbol = await watchlistSymbolData;

  if (rawWatchlistSymbol.err) {
    return rawWatchlistSymbol as SafeType<WatchlistSymbol>;
  } else {
    if (rawWatchlistSymbol.ok?.data) {
      const symbols = parseSymbols(rawWatchlistSymbol.ok.data);
      const result = symbols[0];
      return { ok: result };
    } else {
      return { err: new SafeError('did not get a valid watchlist symbol', 'invalid_watchlist_symbol') };
    }
  }
};

export const ingestWatchlist = async (
  watchlistData: SafePromise<IncomingWatchlistResult>,
): SafePromise<Watchlist[]> => {
  const rawWatchlists = await watchlistData;
  if (rawWatchlists.err) {
    return rawWatchlists as SafeType<Watchlist[]>;
  } else {
    if (rawWatchlists.ok?.data) {
      return {
        ok: Object.values(rawWatchlists.ok.data).map(ingestCreatedWatchlist),
      };
    } else {
      return { err: new SafeError('did not get a valid watchlist', 'invalid_watchlist') };
    }
  }
};

export const ingestCreatedWatchlist = (watchlistData: IncomingWatchlist): Watchlist => {
  return {
    emailSummary: !!watchlistData.send_summary,
    name: watchlistData.portfolio_name,
    realtimeBZ: !!watchlistData.realtime_bz,
    realtimePR: !!watchlistData.realtime_pr,
    realtimeSEC: !!watchlistData.realtime_sec,
    sound: watchlistData.sound,
    symbols: parseSymbols(watchlistData.alerts ?? {}),
    watchlistId: watchlistData.portfolio_id,
  };
};
